<template>
  <a-row class="profile__info">
    <a-col :span="24">
      <a-row>
        <a-col>
          <div class="profile-avatar">
            <div class="profile-avatar__image">
              <img
                :src="getAvatarUrl(executor.avatar)"
                alt=""
                v-if="executor.avatar"
              />
              <div class="profile-avatar__skeleton" v-else>
                <img src="@/assets/icons/camera.svg" alt="" />
              </div>
            </div>
          </div>
        </a-col>
        <a-col>
          <div class="profile-description">
            <div class="profile-description__item">
              <div class="profile-description__label">Фамилия:</div>
              <div class="profile-description__content">
                {{ executor.lastName }}
              </div>
            </div>
            <div class="profile-description__item">
              <div class="profile-description__label">Имя:</div>
              <div class="profile-description__content">
                {{ executor.firstName }}
              </div>
            </div>
            <div class="profile-description__item">
              <div class="profile-description__label">Отчество:</div>
              <div class="profile-description__content">
                {{ executor.middleName }}
              </div>
            </div>
            <div class="profile-description__item">
              <div class="profile-description__label">E-mail:</div>
              <div class="profile-description__content">
                {{ executor.email }}
              </div>
            </div>
            <div class="profile-description__item">
              <div class="profile-description__label">Телефон:</div>
              <div class="profile-description__content">
                {{ executor.phone }}
              </div>
            </div>
            <div class="profile-description__item">
              <SocialIcons :person="executor" />
            </div>
          </div>
        </a-col>
        <a-col class="profile-edit">
          <a @click="emit('close')">К команде</a>
        </a-col>
      </a-row>
    </a-col>
  </a-row>

  <a-divider />

  <a-row>
    <h4>Задачи</h4>
  </a-row>
  <project-stats
    :completed-count="executor.completedCount"
    :completed-overdue-count="executor.completedOverdueCount"
    :incomplete-count="executor.incompleteCount"
    :incomplete-overdue-count="executor.incompleteOverdueCount"
  />

  <task-item-list
    ref="incompleteTaskRef"
    :task-status="PROJECT_TASK_STATUS_ALL_INCOMPLETE"
    :show-availability="isProjectExternal"
    :user-id="executor.userId"
    :disabled="disabled"
    @update="onUpdate"
  >
    <template v-slot:header>
      <span
        >В работе /
        {{ executor.incompleteCount + executor.incompleteOverdueCount }}</span
      >
    </template>
  </task-item-list>

  <task-item-list
    ref="completedTaskRef"
    :task-status="PROJECT_TASK_STATUS_ALL_COMPLETED"
    :show-availability="isProjectExternal"
    :user-id="executor.userId"
    :disabled="disabled"
    @update="onUpdate"
  >
    <template v-slot:header>
      <span
        >Выполнено /
        {{ executor.completedCount + executor.completedOverdueCount }}</span
      >
    </template>
  </task-item-list>
</template>

<script setup>
  import SocialIcons from '@/components/social/SocialIcons.vue';
  import ProjectStats from '@/components/projects/management/ProjectStats.vue';
  import {
    PROJECT_TASK_STATUS_ALL_COMPLETED,
    PROJECT_TASK_STATUS_ALL_INCOMPLETE,
  } from '@/api/models/ProjectTaskStatus';
  import TaskItemList from '@/components/projects/management/TaskItemList.vue';
  import { onMounted, ref } from 'vue';

  const emit = defineEmits(['update', 'close']);
  defineProps({
    executor: Object,
    disabled: {
      type: Boolean,
      default: false,
    },
    isProjectExternal: {
      type: Boolean,
      default: false,
    },
  });
  const completedTaskRef = ref(null);
  const incompleteTaskRef = ref(null);

  onMounted(async () => {
    await completedTaskRef.value.updateTasks();
    await incompleteTaskRef.value.updateTasks();
  });

  const getAvatarUrl = (avatar) =>
    avatar ? `/api/v1/attachment/${avatar}` : null;

  const onUpdate = async () => {
    emit('update');
    await completedTaskRef.value.updateTasks();
    await incompleteTaskRef.value.updateTasks();
  };
</script>

<style lang="scss" scoped>
@use "@/assets/styles/_colors.scss" as *;

  * {
    font-family: 'Nunito Sans', sans-serif;
    user-select: none;
  }

  .profile__info {
    padding: 10px 0 44px 0;

    .profile-avatar {
      height: 180px;
      width: 180px;

      .profile-avatar__image {
        position: absolute;

        img {
          border-radius: 12px;
          display: block;
          resize: both;
          height: 100%;
          width: 100%;
        }

        .profile-avatar__skeleton {
          align-items: center;
          background-color: $color-bg-secondary-grey;
          border-radius: 12px;
          display: flex;
          height: 170px;
          justify-content: center;
          width: 170px;

          img {
            width: 27px;
            height: 23px;
          }
        }
      }

      .profile-avatar__button {
        border-radius: 8px;
        background-color: $color-bg-primary-white;
        box-shadow: 0 2px 5.5px 0 rgba(0, 0, 0, 0.06);
        cursor: pointer;
        height: 55px;
        width: 55px;
        display: flex;
        align-items: center;
        justify-content: center;
        position: absolute;
        right: 0;
        bottom: 0;

        &:hover {
          background-color: $color-btn-special-hover;

          img {
            filter: sepia(100%) brightness(200%);
          }
        }

        img {
          width: 19px;
          height: 19px;
        }
      }
    }

    .profile-description {
      color: $color-text-secondary-1;
      display: flex;
      flex-direction: column;
      gap: 14px 0;
      margin-left: 50px;
      user-select: none;

      .profile-description__item {
        display: flex;
        gap: 0 10px;

        .profile-description__label {
          font: 700 14px/20px 'Nunito Sans';
        }

        .profile-description__content {
          font: 400 14px/20px 'Nunito Sans';
        }

        .profile-description__socials {
          cursor: pointer;
        }
      }
    }

    .profile-edit {
      flex: 1;
      text-align: end;

      a {
        color: $color-link-primary;
        font: 400 14px/19px 'Nunito Sans';
        text-decoration: underline;
        text-underline-position: under;
      }
    }
  }

  .profile__tasks-counter {
    flex-wrap: nowrap;
    height: 159px;

    .tasks-counter {
      align-items: center;
      display: flex;
      flex-direction: column;
      justify-content: center;
      row-gap: 10px;
      width: 100%;

      &.total {
        background-color: $color-bg-seconary-black-95;
      }

      &.inwork {
        background-color: $color-btn-secondary;
        h4 {
          color: $color-bg-primary-black-100;
        }

        .tasks-counter__num {
          color: $color-bg-primary-black-100;
        }
      }

      &.completed {
        background-color: $color-bg-status-done;
      }

      &.expired {
        background-color: $color-bg-status-expired;
      }

      &.incompleted {
        background-color: $color-bg-status-reject;
      }

      h4 {
        color: $color-bg-primary-white;
      }

      &__num {
        color: $color-bg-primary-white;
        font-size: 62px;
        font-weight: 700;
        line-height: 72px;
      }
    }
  }

  .profile__tasks-label {
    align-items: center;
    background-color: $color-bg-primary-black-100;
    color: $color-bg-primary-white;
    display: flex;
    height: 32px;
    justify-content: center;
    margin: 40px 0 20px 0;
    text-align: center;
  }
</style>
